/*
 * memoryRoundtrip — Markdown-String ⇄ Paragraph-Liste fuer den Memory-Editor
 * (Spec §4).
 *
 * Das User-Memory wird in `users.global_memory` als Plain-Text-Markdown
 * gespeichert. Im Editor brauchen wir das Modell als Paragraphen (jede Zeile
 * ein Paragraph), damit ProseEditor.setParagraphsRT() es uebernehmen kann.
 *
 * Konvention:
 *   - Block-Trenner ist Doppel-Newline (`\n\s*\n+`); innerhalb eines Blocks
 *     ist jede Zeile ein eigener Paragraph.
 *   - Beim Schreiben werden konsekutive Listen-Items DESSELBEN Listen-Typs
 *     mit `\n` verbunden, alles andere mit `\n\n` (kanonisches Markdown-
 *     Layout, das die AI auch generiert).
 *   - Separatoren (type === 'separator') koennen hier nie entstehen — der
 *     Memory-Editor hat keine Section-Hierarchie. Der Wrapper-Test in
 *     Phase 3 stellt das sicher.
 *
 * Roundtrip-Idempotenz: Fuer kanonisches Markdown (mit Leerzeile zwischen
 * verschiedenen Block-Typen) gilt
 *   paragraphsToMemoryString(memoryStringToParagraphs(s)) === s.
 * Nicht-kanonische Inputs (z.B. zwei direkt benachbarte Headings ohne
 * Leerzeile) werden beim Roundtrip in das kanonische Format normalisiert —
 * das ist der bewusste Trade-off (Spec §4 Edge-Cases).
 */

import {
    markdownToRichText, richTextToMarkdown, richTextToMarkdownFull
} from '../RichTextOps.js'

/**
 * Zerlegt einen Memory-Markdown-String in Paragraph-Daten, die direkt von
 * `EditorModel.setParagraphsRT(...)` uebernommen werden koennen.
 *
 * @param {string} memoryString
 * @returns {Array<object>}
 */
export function memoryStringToParagraphs(memoryString) {
    if (typeof memoryString !== 'string' || memoryString.length === 0) {
        return [emptyParagraph()]
    }

    // Single-Pass über alle Zeilen mit Fenced-Code-Block-State. Außerhalb
    // eines Fences wirken Leerzeilen als Block-Trenner und werden nicht zu
    // Paragraphs (entspricht dem alten /\n\s*\n+/-Split). Innerhalb eines
    // Fences bleibt jede Zeile — auch leer — eine code-Inhaltszeile.
    const allLines = memoryString.split('\n')
    const paragraphs = []
    let inFence = false
    for (const rawLine of allLines) {
        if (/^```/.test(rawLine)) {
            inFence = !inFence
            continue
        }
        if (inFence) {
            paragraphs.push(codeLineToParagraphData(rawLine))
            continue
        }
        if (rawLine.trim() === '') continue
        paragraphs.push(lineToParagraphData(rawLine))
    }

    if (paragraphs.length === 0) return [emptyParagraph()]
    return paragraphs
}

/**
 * Serialisiert eine Paragraph-Liste zurueck zu einem Markdown-String. Konsekutive
 * Listen-Items desselben Typs bleiben als ein Block (mit `\n`), Block-Wechsel
 * werden mit `\n\n` getrennt.
 *
 * @param {Array<object>} paragraphs - serialisierte Paragraph-Daten (wie sie
 *   `serializeParagraphRT` produziert) ODER vollstaendige Paragraph-Instanzen
 *   (Felder werden uebernommen)
 * @returns {string}
 */
export function paragraphsToMemoryString(paragraphs) {
    if (!Array.isArray(paragraphs) || paragraphs.length === 0) return ''

    // Numbered-Items brauchen einen laufenden Counter, weil
    // richTextToMarkdownFull pauschal '1.' schreibt — Markdown-Konvention,
    // aber fuer unsere String-Idempotenz brauchen wir 1, 2, 3, ...
    // Verschachtelte Listen brauchen einen Counter PRO Level (Stack), damit
    // beim Zuruecksprung auf einen flacheren Level dort weitergezaehlt wird:
    //   1. eins
    //      1. unter
    //   2. zwei      ← muss "2." bleiben, nicht "1."
    // Stack wird zurueckgesetzt, wenn der numbered-Block endet (Plain/Bullet
    // dazwischen, oder anderer listType).
    /** @type {number[]} counters[level] = letzter ausgegebener Index auf diesem Level */
    let counters = []
    const lines = []
    for (let i = 0; i < paragraphs.length; i++) {
        const p = paragraphs[i] || {}
        if (p.listType === 'numbered') {
            const level = p.listLevel || 0
            const prev = paragraphs[i - 1]
            const prevIsNumbered = !!(prev && prev.listType === 'numbered')
            const prevLevel = prevIsNumbered ? (prev.listLevel || 0) : -1

            if (!prevIsNumbered) {
                counters = [] // neuer numbered-Block
                counters[level] = 1
            } else if (level > prevLevel) {
                counters[level] = 1 // tiefer geschachtelt → frischer Sub-Counter
            } else {
                if (level < prevLevel) counters.length = level + 1 // flacher → Sub-Counter wegwerfen
                counters[level] = (counters[level] || 0) + 1
            }
            lines.push(paragraphDataToLine(p, counters[level]))
        } else {
            counters = []
            lines.push(paragraphDataToLine(p, 0))
        }
    }

    // Aufeinanderfolgende code-Paragraphs werden zu einem ` ``` `-Block
    // zusammengefasst. Innerhalb des Blocks trennen wir mit \n, an den
    // Block-Grenzen mit \n\n (außerhalb) bzw. eingerahmten ``` -Linien.
    let out = ''
    let inCode = false
    for (let i = 0; i < paragraphs.length; i++) {
        const curr = paragraphs[i] || {}
        const currIsCode = !!curr.codeBlock
        if (i === 0) {
            if (currIsCode) {
                out += '```\n'
                inCode = true
            }
        } else {
            const prev = paragraphs[i - 1] || {}
            const prevIsCode = !!prev.codeBlock
            if (prevIsCode && currIsCode) {
                out += '\n'
            } else if (prevIsCode && !currIsCode) {
                out += '\n```\n\n'
                inCode = false
            } else if (!prevIsCode && currIsCode) {
                const sameList = prev.listType && curr.listType
                    && prev.listType === curr.listType
                out += (sameList ? '\n' : '\n\n')
                out += '```\n'
                inCode = true
            } else {
                const sameList = prev.listType && curr.listType
                    && prev.listType === curr.listType
                out += sameList ? '\n' : '\n\n'
            }
        }
        out += lines[i]
    }
    if (inCode) out += '\n```'
    return out
}

// ─── interne Helfer ───────────────────────────────────────────────────────

function emptyParagraph() {
    return {
        text: '',
        outlineDepth: 0
    }
}

function lineToParagraphData(line) {
    const rich = markdownToRichText(line)
    const para = {
        text: rich.text || '',
        outlineDepth: 0
    }
    // Formats / Links nur einsetzen, wenn nicht-leer — das deckt sich mit
    // dem Kompakt-Format aus serializeParagraphRT, sodass der spaetere
    // Roundtrip ueber Persist-Pfade nicht extra aufgeblaeht wird.
    if (rich.formats && rich.formats.length > 0) {
        para.formats = { text: rich.formats }
    }
    if (rich.links && rich.links.length > 0) {
        para.links = { text: rich.links }
    }
    if (rich.headingLevel > 0) para.headingLevel = rich.headingLevel
    if (rich.listType) para.listType = rich.listType
    if (rich.listLevel > 0) para.listLevel = rich.listLevel
    if (rich.blockquote) para.blockquote = true
    return para
}

// Code-Block-Inhaltszeile: raw Text ohne Inline-Format-Parsing, codeBlock=true.
function codeLineToParagraphData(line) {
    return {
        text: line,
        outlineDepth: 0,
        codeBlock: true
    }
}

function paragraphDataToLine(p, numberedCounter) {
    if (!p) return ''
    // Code-Paragraphs sind raw — keine Markdown-Konvertierung. Der ``` -Wrap
    // wird vom äußeren Block-Trenner-Loop in paragraphsToMemoryString gesetzt.
    if (p.codeBlock) return p.text || ''
    const text = p.text || ''
    const formats = (p.formats && p.formats.text) ? p.formats.text : []
    const links = (p.links && p.links.text) ? p.links.text : []

    // Numbered: eigenen Prefix bauen, sonst pauschalisiert richTextToMarkdownFull
    // alle Items auf '1.'.
    if (p.listType === 'numbered') {
        const inline = richTextToMarkdown(text, formats, links)
        let prefix = ''
        if (p.blockquote) prefix += '> '
        if (p.headingLevel > 0) prefix += '#'.repeat(p.headingLevel) + ' '
        prefix += '  '.repeat(p.listLevel || 0) + `${numberedCounter}. `
        return prefix + inline
    }

    return richTextToMarkdownFull(
        text, formats,
        p.headingLevel || 0,
        p.listType || null,
        p.listLevel || 0,
        p.blockquote || false,
        links)
}
